import {
  updateAsyncTaskGanttV2,
  findDeepGetTaskRecursive
} from '../../GanttVisualization.helper';
import { renderLeanToSelect, renderSelectedOption } from './templates';
import {
  calculateLeanStatus,
  notifyMessage
} from '../../../../utils/lookahead-common';
import {
  save_single_task_lookahead as saveSingleTaskLookahead,
  workerService
} from '../../../../services/serviceworker.service';
import { taskService } from '../../../../services';
import { LEAN_STATUS } from '../../../../constants/leanStatus.constants.js';
import { hasUnreleasedConstraints } from '../../../../views/lookahead/planification/index.helper';

export const prepareUIElements = (options) => {
  const { selectedObject, optionsList, isOnlyReadElement, column, task } =
    options;

  const selectedLeanStatus = selectedObject.lean_status
    ? optionsList.find(
        (priorities) => priorities.value === selectedObject.lean_status
      )
    : null;

  const isReadOnly =
    isOnlyReadElement ||
    selectedObject.lean_status === LEAN_STATUS.COMMITTED ||
    selectedObject.lean_status === LEAN_STATUS.RESTRICTED;

  const placeholderHtml = selectedLeanStatus
    ? renderSelectedOption(selectedLeanStatus, isReadOnly, task)
    : null;

  const optionsListHtml =
    selectedObject.lean_status === LEAN_STATUS.COMMITTED
      ? ''
      : optionsList
          .map((option) => {
            if (
              option.value !== LEAN_STATUS.COMMITTED &&
              option.value !== LEAN_STATUS.RESTRICTED
            ) {
              return !task.isInsideWeekly &&
                option.value === LEAN_STATUS.WEEKLY_PLAN
                ? null
                : `
              <li class="dropdown-option items-list" style="cursor: pointer; list-style: none;"
                  data-action="leanStatusSelection" data-value="${option.value}">
                ${renderLeanToSelect({ object: option }, column, selectedLeanStatus)}
              </li>
            `;
            }
          })
          .join('');

  return { placeholderHtml, optionsListHtml, isReadOnly };
};

export const registerTaskCallbacks = (taskId, task, options) => {
  const { taskFromLookahead, column, gantt, parentTask, activityReference, t } =
    options;
  window.leanCallbacks = window.leanCallbacks || {};
  window.leanCallbacks[taskId] = {
    selection: (id) => {
      const childrenHaveLeanStatusWill = taskFromLookahead.children.some(
        (el) => el.lean_status === LEAN_STATUS.WEEKLY_PLAN
      );
      if (childrenHaveLeanStatusWill === false) {
        task.inside_weekly_other_lean = false;

        if (task.isInsideWeekly && options.value !== LEAN_STATUS.WEEKLY_PLAN) {
          task.inside_weekly_other_lean = true;
        }
        taskFromLookahead.inside_weekly_other_lean =
          task.inside_weekly_other_lean;
        updateAsyncTaskGanttV2(taskFromLookahead, column.name, id, gantt);
        const updateDataLeanStatus = (taskCallback) => {
          updateAsyncTaskGanttV2(taskCallback, 'lean_status', null, gantt);
        };
        calculateLeanStatus(
          task,
          parentTask || activityReference,
          activityReference,
          updateDataLeanStatus,
          null
        );
      } else {
        notifyMessage({
          title: t('modules.weeklyplan'),
          message: t('have_childs_will'),
          type: 'warning'
        });
      }
    }
  };
};

const handleLeanAction = (action, target, taskId) => {
  const callbacks = window.leanCallbacks && window.leanCallbacks[taskId];
  if (!callbacks) return;
  const actionHandlers = {
    leanStatusSelection: () => {
      const value = target.getAttribute('data-value');
      callbacks.selection && callbacks.selection(value);
    }
  };

  const handler = actionHandlers[action];
  if (handler) {
    handler();
  }
};

export const setupGlobalHandlers = () => {
  window.leanCallbacks = window.leanCallbacks || {};
  if (window._leanGlobalHandler) return;
  const leanGlobalHandler = (event) => {
    const target = event.target.closest('[data-action]');
    if (!target) return;
    const action = target.getAttribute('data-action');
    const container = target.closest('[data-task-id]');
    if (!container) return;
    const taskId = container.getAttribute('data-task-id');
    handleLeanAction(action, target, taskId);
    event.stopPropagation();
  };
  document.addEventListener('click', leanGlobalHandler);
  window._leanGlobalHandler = leanGlobalHandler;
};

export const cleanupLeanGlobalHandlers = () => {
  if (window._leanGlobalHandler) {
    document.removeEventListener('click', window._leanGlobalHandler);
    delete window._leanGlobalHandler;
  }
};

const handleConstraintStatus = (task, taskFromLookahead) => {
  const hasUnreleased = hasUnreleasedConstraints(taskFromLookahead);

  if (
    hasUnreleased &&
    taskFromLookahead.lean_status !== LEAN_STATUS.RESTRICTED
  ) {
    taskFromLookahead.status_before_restricted = taskFromLookahead.lean_status;
    taskFromLookahead.lean_status = LEAN_STATUS.RESTRICTED;
    taskFromLookahead.restricted = true;

    task.status_before_restricted = task.lean_status;
    task.lean_status = LEAN_STATUS.RESTRICTED;
    task.restricted = true;

    return true;
  }

  if (!hasUnreleased && taskFromLookahead.restricted) {
    taskFromLookahead.lean_status =
      taskFromLookahead.status_before_restricted || task.lean_status;
    taskFromLookahead.restricted = false;

    task.lean_status = taskFromLookahead.lean_status;
    task.restricted = false;

    return true;
  }

  return false;
};

const syncLeanStatus = (task, taskFromLookahead) => {
  if (taskFromLookahead.lean_status !== task.lean_status) {
    taskFromLookahead.lean_status = task.lean_status;
    return true;
  }
  return false;
};

const handleWeeklyStatus = (task, taskFromLookahead) => {
  if (
    task.checkedIfInsideWeekly &&
    task.inside_weekly_other_lean &&
    !task.isInsideWeekly
  ) {
    task.inside_weekly_other_lean = false;
    taskFromLookahead.inside_weekly_other_lean = task.inside_weekly_other_lean;
    return true;
  }
  return false;
};

const saveTaskChanges = (taskFromLookahead) => {
  workerService.callAction(saveSingleTaskLookahead, taskFromLookahead, () => {
    taskService.update(taskFromLookahead);
  });
};

export const updateTask = (task, taskFromLookahead) => {
  if (handleConstraintStatus(task, taskFromLookahead)) {
    saveTaskChanges(taskFromLookahead);
    return;
  }

  const needsStatusSync = syncLeanStatus(task, taskFromLookahead);

  const needsWeeklySync = handleWeeklyStatus(task, taskFromLookahead);

  if (needsStatusSync || needsWeeklySync) {
    saveTaskChanges(taskFromLookahead);
  }
};

export const getParentTask = (taskFromLookahead, activities) => {
  if (taskFromLookahead && taskFromLookahead.parent_id) {
    const doesExistAtReferenceParent = findDeepGetTaskRecursive(
      activities,
      'id',
      taskFromLookahead.parent_id
    );
    return doesExistAtReferenceParent || null;
  }
  return null;
};

export const capitalizeText = (text) => {
  const stringInLowerCase = text.toLowerCase();
  return stringInLowerCase.charAt(0).toUpperCase() + stringInLowerCase.slice(1);
};
